import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts"
import { useToast } from "src/components/ui/toast"
import { fetchVelocity, exportUrl } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, CHART_COLORS } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import VelocityBottleneckChart from "src/features/analytics/components/VelocityBottleneckChart"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

export default function VelocityReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("90d")
  const [filters, setFilters] = useState(presetToRange("90d"))
  const { data, loading, fetch } = useReport(fetchVelocity)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id, filters.pipeline_id])

  const summary = data?.summary

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
        exportUrl={exportUrl("velocity", filters)}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !summary?.won_count ? (
        <EmptyReportState message="No won deals in this period. Win deals to see velocity metrics." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard label="Avg Days to Win" value={summary.avg_days_to_win ?? "—"} suffix="days" />
            <KpiCard label="Median Days" value={summary.median_days_to_win ?? "—"} suffix="days" />
            <KpiCard label="Lead → Deal" value={summary.avg_days_lead_to_deal ?? "—"} suffix="days" />
            <KpiCard label="Deals Won" value={summary.won_count} />
          </div>

          <div className="mb-5">
            <VelocityBottleneckChart byStage={data.by_stage} />
          </div>

          {/* Velocity trend */}
          {data.trend?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 text-[13px] font-medium text-slate-700">Monthly Trend</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit="d" />
                  <Tooltip formatter={(v) => [`${v} days`]} />
                  <Line
                    dataKey="avg_days_to_win"
                    name="Avg days to win"
                    stroke={CHART_COLORS.primary}
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By stage table */}
          {data.by_stage?.length > 0 && (
            <div className="mt-5 rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">Time per Stage</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Stage</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Avg</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">P50</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">P90</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_stage.map((s) => (
                    <TableRow key={s.stage.id}>
                      <TableCell className="py-2.5 text-slate-900">{s.stage.name}</TableCell>
                      <TableCell className="py-2.5 text-right">{s.avg_days != null ? `${s.avg_days}d` : "—"}</TableCell>
                      <TableCell className="py-2.5 text-right">{s.p50 != null ? `${s.p50}d` : "—"}</TableCell>
                      <TableCell className="py-2.5 text-right">{s.p90 != null ? `${s.p90}d` : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
